import React, { useState, useEffect } from 'react';
import {
    Box,
    TextField,
    Button,
    Typography,
    Paper,
    Card,
    CardContent,
    Stack,
    Divider,
    IconButton,
    InputAdornment,
    Tooltip,
    Alert,
    CircularProgress,
    FormControl,
    FormLabel,
    RadioGroup,
    FormControlLabel,
    Radio,
    Tabs,
    Tab,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Chip
} from '@mui/material';
import {
    Link as LinkIcon,
    ContentCopy,
    Search,
    CheckCircle,
    Error as ErrorIcon,
    Share as ShareIcon,
    WhatsApp,
    History as HistoryIcon,
    AddLink,
    Delete,
    Message,
    Save
} from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import { razorpayAPI, PaymentLinkResponse, PaymentLinkHistoryItem } from '../../api/razorpay';
import { format } from 'date-fns';

const PaymentLinkGenerator: React.FC = () => {
    const [tabValue, setTabValue] = useState(0);
    const { enqueueSnackbar } = useSnackbar();

    // Generator State
    const [orderNumber, setOrderNumber] = useState('');
    const [linkType, setLinkType] = useState<'all' | 'upi'>('all');
    const [genLoading, setGenLoading] = useState(false);
    const [result, setResult] = useState<PaymentLinkResponse | null>(null);
    const [genError, setGenError] = useState<string | null>(null);

    // History State
    const [history, setHistory] = useState<PaymentLinkHistoryItem[]>([]);
    const [histLoading, setHistLoading] = useState(false);

    // Settings State
    const [messageTemplate, setMessageTemplate] = useState('');
    const [settingsLoading, setSettingsLoading] = useState(false);

    useEffect(() => {
        fetchSettings();
    }, []);

    const fetchSettings = async () => {
        try {
            const data = await razorpayAPI.getMessageSettings();
            if (data.message) setMessageTemplate(data.message);
        } catch (err) {
            console.error(err);
        }
    };

    const handleSaveSettings = async () => {
        if (!messageTemplate.trim()) {
            enqueueSnackbar('Message template cannot be empty', { variant: 'error' });
            return;
        }
        setSettingsLoading(true);
        try {
            await razorpayAPI.updateMessageSettings(messageTemplate);
            enqueueSnackbar('Message settings saved successfully', { variant: 'success' });
        } catch (err) {
            console.error(err);
            enqueueSnackbar('Failed to save message settings', { variant: 'error' });
        } finally {
            setSettingsLoading(false);
        }
    };

    const formatMessage = (template: string, data: any) => {
        let msg = template || '';
        const replacements: Record<string, string> = {
            '{order_id}': data.order_id ? `#${data.order_id}` : '#12345',
            '{items_count}': String(data.items_count || '5'),
            '{amount}': data.amount ? `₹${data.amount.toLocaleString()}` : '₹1,500',
            '{payment_link}': data.payment_link || 'https://rzp.io/l/example',
            '{delivery_date}': data.delivery_date || 'Tomorrow',
            '{delivery_slot}': data.delivery_slot || 'Morning'
        };

        Object.entries(replacements).forEach(([key, value]) => {
            // Escape special regex characters in the key (like { and })
            const escapedKey = key.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
            msg = msg.replace(new RegExp(escapedKey, 'g'), value);
        });
        return msg;
    };

    const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
        setTabValue(newValue);
        if (newValue === 1) {
            fetchHistory();
        }
    };

    const fetchHistory = async () => {
        setHistLoading(true);
        try {
            const data = await razorpayAPI.getHistory(50, 0);
            setHistory(data.items);
        } catch (err) {
            console.error(err);
            enqueueSnackbar('Failed to load history', { variant: 'error' });
        } finally {
            setHistLoading(false);
        }
    };

    const handleGenerate = async () => {
        if (!orderNumber.trim()) {
            setGenError('Please enter an order number');
            return;
        }

        setGenLoading(true);
        setGenError(null);
        setResult(null);

        try {
            const data = await razorpayAPI.createPaymentLink({
                order_number: orderNumber.trim(),
                link_type: linkType
            });
            setResult(data);
            enqueueSnackbar('Payment link generated successfully', { variant: 'success' });
            // Refresh history if we switch tabs later
            // We can also auto-switch to history, but staying on result is often better
        } catch (err: any) {
            console.error(err);
            const msg = err.response?.data?.detail || 'Failed to generate payment link';
            setGenError(msg);
            enqueueSnackbar(msg, { variant: 'error' });
        } finally {
            setGenLoading(false);
        }
    };

    const handleDelete = async (id: number) => {
        if (!window.confirm('Are you sure you want to delete this payment link from history? If it is still active, it will be cancelled.')) {
            return;
        }

        try {
            await razorpayAPI.deleteHistoryItem(id);
            enqueueSnackbar('Payment link deleted', { variant: 'success' });
            fetchHistory(); // Refresh list
        } catch (err: any) {
            console.error(err);
            enqueueSnackbar('Failed to delete history item', { variant: 'error' });
        }
    };

    const handleCopy = (text: string, successMessage: string = 'Link copied to clipboard') => {
        navigator.clipboard.writeText(text);
        enqueueSnackbar(successMessage, { variant: 'success' });
    };

    const handleShareWhatsApp = (link: string, orderId: string, customText?: string) => {
        const text = customText || `Hi, please complete your payment for Order #${orderId} using this link: ${link}`;
        const url = `https://wa.me/?text=${encodeURIComponent(text)}`;
        window.open(url, '_blank');
    };

    const getStatusColor = (status: string) => {
        switch (status) {
            case 'paid': return 'success';
            case 'created': return 'info';
            case 'expired': return 'error';
            case 'cancelled': return 'warning';
            default: return 'default';
        }
    };

    return (
        <Box sx={{ p: 3, maxWidth: 900, mx: 'auto' }}>
            <Typography variant="h4" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <LinkIcon fontSize="large" color="primary" />
                Payment Link Manager
            </Typography>
            <Typography variant="body1" color="text.secondary" paragraph>
                Generate Razorpay payment links and track their status. Generated links expire in 12 hours.
            </Typography>

            <Alert severity="info" sx={{ mb: 2 }}>
                Payment links are valid for 12 hours from the time of generation.
            </Alert>

            <Paper sx={{ mt: 3 }}>
                <Tabs value={tabValue} onChange={handleTabChange} variant="fullWidth">
                    <Tab icon={<AddLink />} label="Generate New Link" />
                    <Tab icon={<HistoryIcon />} label="Link History" />
                    <Tab icon={<Message />} label="Message Settings" />
                </Tabs>

                {/* GENERATOR TAB */}
                {tabValue === 0 && (
                    <Box sx={{ p: 4, maxWidth: 600, mx: 'auto' }}>
                        <Stack spacing={3}>
                            <TextField
                                label="Order Number"
                                placeholder="e.g. 12345"
                                value={orderNumber}
                                onChange={(e) => setOrderNumber(e.target.value)}
                                onKeyPress={(e) => e.key === 'Enter' && handleGenerate()}
                                fullWidth
                                variant="outlined"
                                InputProps={{
                                    startAdornment: (
                                        <InputAdornment position="start">
                                            <Search color="action" />
                                        </InputAdornment>
                                    ),
                                }}
                            />

                            <FormControl component="fieldset">
                                <FormLabel component="legend">Payment Mode</FormLabel>
                                <RadioGroup
                                    row
                                    name="linkType"
                                    value={linkType}
                                    onChange={(e) => setLinkType(e.target.value as 'all' | 'upi')}
                                >
                                    <FormControlLabel value="all" control={<Radio />} label="All Modes" />
                                    <FormControlLabel value="upi" control={<Radio />} label="UPI Only" />
                                </RadioGroup>
                            </FormControl>

                            <Button
                                variant="contained"
                                size="large"
                                onClick={handleGenerate}
                                disabled={genLoading}
                                startIcon={genLoading ? <CircularProgress size={20} /> : <LinkIcon />}
                            >
                                {genLoading ? 'Generating...' : 'Generate Payment Link'}
                            </Button>

                            {genError && (
                                <Alert severity="error" icon={<ErrorIcon />}>
                                    {genError}
                                </Alert>
                            )}

                            {result && (
                                <Card variant="outlined" sx={{ bgcolor: 'action.hover', borderColor: 'primary.main' }}>
                                    <CardContent>
                                        <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                                            GENERATED LINK FOR ORDER #{result.order_id}
                                        </Typography>

                                        <Box sx={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            bgcolor: 'white',
                                            p: 1.5,
                                            borderRadius: 1,
                                            border: '1px dashed',
                                            borderColor: 'divider',
                                            mb: 2
                                        }}>
                                            <LinkIcon sx={{ mr: 1, color: 'text.secondary', fontSize: 20 }} />
                                            <Typography
                                                variant="body1"
                                                sx={{
                                                    flexGrow: 1,
                                                    fontFamily: 'monospace',
                                                    fontWeight: 'bold',
                                                    color: 'primary.main',
                                                    overflow: 'hidden',
                                                    textOverflow: 'ellipsis'
                                                }}
                                            >
                                                {result.payment_link}
                                            </Typography>
                                            <Tooltip title="Copy Link">
                                                <IconButton size="small" onClick={() => handleCopy(result.payment_link)}>
                                                    <ContentCopy fontSize="small" />
                                                </IconButton>
                                            </Tooltip>
                                        </Box>

                                        <Stack direction="row" spacing={2} alignItems="center">
                                            <Box>
                                                <Typography variant="caption" display="block" color="text.secondary">
                                                    Amount
                                                </Typography>
                                                <Typography variant="h6">
                                                    ₹{result.amount.toLocaleString()}
                                                </Typography>
                                            </Box>

                                            <Divider orientation="vertical" flexItem />

                                            <Box>
                                                <Typography variant="caption" display="block" color="text.secondary">
                                                    Status
                                                </Typography>
                                                <Stack direction="row" spacing={0.5} alignItems="center">
                                                    <CheckCircle fontSize="small" color="success" />
                                                    <Typography variant="body2" fontWeight="medium">
                                                        {result.status.toUpperCase()}
                                                    </Typography>
                                                </Stack>
                                            </Box>

                                            <Box sx={{ flexGrow: 1 }} />

                                            <Button
                                                variant="outlined"
                                                startIcon={<WhatsApp />}
                                                color="success"
                                                onClick={() => handleShareWhatsApp(
                                                    result.payment_link,
                                                    result.order_id,
                                                    formatMessage(messageTemplate, result)
                                                )}
                                                size="small"
                                            >
                                                Share
                                            </Button>
                                        </Stack>

                                        <Divider sx={{ my: 2 }} />

                                        <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                                            SHAREABLE MESSAGE
                                        </Typography>

                                        <Box sx={{
                                            position: 'relative',
                                            bgcolor: 'grey.50',
                                            p: 2,
                                            borderRadius: 1,
                                            border: '1px solid',
                                            borderColor: 'divider'
                                        }}>
                                            <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', fontFamily: 'inherit', pr: 4, color: 'text.primary' }}>
                                                {formatMessage(messageTemplate, result)}
                                            </Typography>
                                            <Tooltip title="Copy Message">
                                                <IconButton
                                                    size="small"
                                                    sx={{ position: 'absolute', top: 8, right: 8 }}
                                                    onClick={() => handleCopy(
                                                        formatMessage(messageTemplate, result),
                                                        'Message copied to clipboard'
                                                    )}
                                                >
                                                    <ContentCopy fontSize="small" />
                                                </IconButton>
                                            </Tooltip>
                                        </Box>
                                    </CardContent>
                                </Card>
                            )}
                        </Stack>
                    </Box>
                )}

                {/* HISTORY TAB */}
                {tabValue === 1 && (
                    <Box sx={{ p: 0 }}>
                        {histLoading ? (
                            <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                                <CircularProgress />
                            </Box>
                        ) : (
                            <TableContainer>
                                <Table>
                                    <TableHead>
                                        <TableRow sx={{ bgcolor: 'action.hover' }}>
                                            <TableCell>Date</TableCell>
                                            <TableCell>Order #</TableCell>
                                            <TableCell>Customer</TableCell>
                                            <TableCell>Amount</TableCell>
                                            <TableCell>Status</TableCell>
                                            <TableCell>Link</TableCell>
                                            <TableCell align="right">Actions</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {history.length === 0 ? (
                                            <TableRow>
                                                <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                                                    <Typography color="text.secondary">No payment links generated yet</Typography>
                                                </TableCell>
                                            </TableRow>
                                        ) : (
                                            history.map((row) => (
                                                <TableRow key={row.id} hover>
                                                    <TableCell>
                                                        {row.created_at ? format(new Date(row.created_at), 'dd MMM yyyy, HH:mm') : '-'}
                                                    </TableCell>
                                                    <TableCell>#{row.order_number || row.order_id}</TableCell>
                                                    <TableCell>{row.customer_name || 'N/A'}</TableCell>
                                                    <TableCell>₹{Number(row.amount).toLocaleString()}</TableCell>
                                                    <TableCell>
                                                        <Chip
                                                            label={row.status.toUpperCase()}
                                                            color={getStatusColor(row.status) as any}
                                                            size="small"
                                                        />
                                                    </TableCell>
                                                    <TableCell sx={{ maxWidth: 200 }}>
                                                        <Typography variant="caption" noWrap display="block" color="primary">
                                                            {row.short_url}
                                                        </Typography>
                                                    </TableCell>
                                                    <TableCell align="right">
                                                        <Stack direction="row" spacing={1} justifyContent="flex-end">
                                                            <Tooltip title="Copy Link">
                                                                <IconButton size="small" onClick={() => handleCopy(row.short_url)}>
                                                                    <ContentCopy fontSize="small" />
                                                                </IconButton>
                                                            </Tooltip>
                                                            <Tooltip title="Share on WhatsApp">
                                                                <IconButton size="small" color="success" onClick={() => handleShareWhatsApp(row.short_url, row.order_number)}>
                                                                    <WhatsApp fontSize="small" />
                                                                </IconButton>
                                                            </Tooltip>
                                                            {(row.status === 'created' || row.status === 'expired') && (
                                                                <Tooltip title="Delete from History">
                                                                    <IconButton size="small" color="error" onClick={() => handleDelete(row.id)}>
                                                                        <Delete fontSize="small" />
                                                                    </IconButton>
                                                                </Tooltip>
                                                            )}
                                                        </Stack>
                                                    </TableCell>
                                                </TableRow>
                                            ))
                                        )}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        )}
                    </Box>
                )}

                {/* SETTINGS TAB */}
                {tabValue === 2 && (
                    <Box sx={{ p: 4, maxWidth: 800, mx: 'auto' }}>
                        <Typography variant="h6" gutterBottom>
                            WhatsApp Message Template
                        </Typography>
                        <Typography variant="body2" color="text.secondary" paragraph>
                            Customize the message sent to customers along with the payment link.
                        </Typography>

                        <Stack spacing={3}>
                            <TextField
                                multiline
                                minRows={10}
                                maxRows={15}
                                value={messageTemplate}
                                onChange={(e) => setMessageTemplate(e.target.value)}
                                fullWidth
                                placeholder="Enter message template..."
                                helperText="Available variables: {order_id}, {items_count}, {amount}, {payment_link}, {delivery_date}, {delivery_slot}"
                            />

                            <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                                <Button
                                    variant="contained"
                                    startIcon={settingsLoading ? <CircularProgress size={20} color="inherit" /> : <Save />}
                                    onClick={handleSaveSettings}
                                    disabled={settingsLoading}
                                >
                                    Save Settings
                                </Button>
                            </Box>

                            <Divider />

                            <Typography variant="subtitle2" gutterBottom>
                                PREVIEW
                            </Typography>

                            <Box sx={{
                                position: 'relative',
                                bgcolor: 'grey.50',
                                p: 2,
                                borderRadius: 1,
                                border: '1px solid',
                                borderColor: 'divider'
                            }}>
                                <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', fontFamily: 'inherit', color: 'text.primary' }}>
                                    {formatMessage(messageTemplate, {})}
                                </Typography>
                            </Box>
                        </Stack>
                    </Box>
                )}
            </Paper>
        </Box>
    );
};

export default PaymentLinkGenerator;
